import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import type { Event } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateOnlyMonthDay, formatSqliteUtcMonthDay } from '@/lib/date';

interface ChangelogProps {
  logs: Event[];
}

function formatEventDate(event: Event, datePending: string): string {
  if (event.event_type === 'specific' && event.start_date) {
    const startStr = formatDateOnlyMonthDay(event.start_date);
    if (!startStr) {
      return datePending;
    }

    if (event.end_date) {
      const endStr = formatDateOnlyMonthDay(event.end_date);
      return endStr ? `${startStr}-${endStr}` : startStr;
    }
    return startStr;
  }

  if (event.month && event.day) {
    return formatDateOnlyMonthDay(`2000-${String(event.month).padStart(2, '0')}-${String(event.day).padStart(2, '0')}`);
  }

  return datePending;
}

/* Section 04: recent changes as hairline rows; the full list opens in an editorial dialog. */
export function Changelog({ logs }: ChangelogProps) {
  const { t } = useTranslation('public');
  const [showAll, setShowAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  if (logs.length === 0) {
    return null;
  }

  const metaLine = (log: Event) => {
    const updated = formatSqliteUtcMonthDay(log.updated_at) || t('datePending');
    return `${formatEventDate(log, t('datePending'))} · ${t('changelog.updated', { date: updated })}`;
  };

  let toggleLabel = t('expand');
  let listClassName = 'hidden sm:block';
  if (isExpanded) {
    toggleLabel = t('collapse');
    listClassName = 'block';
  }

  return (
    <section aria-labelledby="changelog-heading">
      <div className="section-head">
        <h2 id="changelog-heading" className="m-0">{t('changelog.title')}</h2>
        <button
          type="button"
          className="text-link ml-auto sm:hidden"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="changelog-list"
        >
          {toggleLabel}
        </button>
      </div>
      <div id="changelog-list" className={listClassName}>
        <ul className="m-0 list-none divide-y divide-rule p-0">
          {logs.slice(0, 5).map((log) => (
            <li key={log.id} className="py-3">
              <p className="m-0 text-sm text-ink">{log.title}</p>
              <p className="m-0 mt-1 text-[11px] text-ink-2">{metaLine(log)}</p>
            </li>
          ))}
        </ul>
        {logs.length > 5 && (
          <div className="border-t border-rule pt-3">
            <button type="button" className="text-link" onClick={() => setShowAll(true)}>
              {t('changelog.viewAll', { count: logs.length })}
            </button>
          </div>
        )}
      </div>

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="flex max-h-[80vh] max-w-xl flex-col">
          <DialogHeader>
            <DialogTitle>{t('changelog.all')}</DialogTitle>
          </DialogHeader>
          <ul className="m-0 list-none divide-y divide-rule overflow-y-auto p-0 pr-2">
            {logs.map((log) => (
              <li key={log.id} className="py-3">
                <p className="m-0 text-sm text-ink">{log.title}</p>
                {log.description && (
                  <p className="m-0 mt-1 text-xs leading-relaxed text-ink-2">{log.description}</p>
                )}
                <p className="m-0 mt-1 text-[11px] text-ink-2">{metaLine(log)}</p>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </section>
  );
}
